import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useUnits } from "@/context/UnitContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Send, Eye, Power, Loader2, Search, Plus, Copy, Trash2, Pencil, Target,
  CheckCircle2, XCircle, Clock, AlertTriangle, MessageSquare, Mail, Smartphone,
  ClipboardCheck,
} from "lucide-react";
import { toast } from "sonner";
import ConditionsBuilder from "@/components/ConditionsBuilder";

// Canais disponíveis para segmentos. Somente canais realmente implementados
// aparecem — evita falsas confirmações de envio quando o canal ainda não está pronto.
const CHANNELS = [
  { value: "whatsapp_zapi",   label: "WhatsApp (Z-API — envio automático)", icon: MessageSquare, status: "ativo" },
  { value: "whatsapp_manual", label: "WhatsApp manual (abrir links wa.me)", icon: MessageSquare, status: "ativo" },
];

// Set com valores aceitos — usado pra normalizar segments legados que possam
// ter `channel` fora do enum (ex: "whatsapp" antigo). Evita mismatch entre o
// que o <select> exibe (fallback visual) e o que é enviado no dispatch.
const VALID_CHANNELS = new Set(["whatsapp_zapi", "whatsapp_manual"]);
const normalizeChannel = (raw) => (VALID_CHANNELS.has(raw) ? raw : "whatsapp_zapi");

// 🎯 Palette de variáveis: rótulo user-friendly + placeholder + exemplo. Precisa bater 1:1 com SUPPORTED_TEMPLATE_VARS do backend.
const TEMPLATE_VARS = [
  { key: "primeiro_nome",    label: "Primeiro nome",        example: "Bruna" },
  { key: "nome",             label: "Nome completo",        example: "Bruna Araujo Silva" },
  { key: "data_agendamento", label: "Data do agendamento",  example: "03/07/2026" },
  { key: "hora_agendamento", label: "Horário",              example: "14:30" },
  { key: "servico",          label: "Serviço",              example: "Corte + escova" },
  { key: "profissional",     label: "Profissional",         example: "Renan" },
  { key: "unidade",          label: "Unidade",              example: "Salão Centro" },
  { key: "telefone",         label: "Telefone",             example: "(11) 99999-9999" },
  { key: "valor_em_aberto",  label: "Valor em aberto",      example: "150,00" },
  { key: "descricao",        label: "Descrição do item",    example: "Coloração" },
];

const KNOWN_VAR_KEYS = new Set(TEMPLATE_VARS.map(v => v.key));

// Encontra placeholders {var} ou {{var}} que não estão na lista suportada
function findUnknownVars(template) {
  if (!template) return [];
  const found = new Set();
  const re = /\{\{?\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}?\}/g;
  let m;
  while ((m = re.exec(template)) !== null) {
    if (!KNOWN_VAR_KEYS.has(m[1])) found.add(m[1]);
  }
  return Array.from(found).sort();
}

// Constrói wa.me/{DDI+phone}?text={mensagem_renderizada} pra um cliente específico.
// Usa o ctx que o backend já retornou no preview (mesma renderização que o
// backend faria em manual_ready). Se telefone não tem DDI, assume Brasil (+55).
function waLinkFor(client, msg) {
  const digits = String(client.phone_norm || "").replace(/\D/g, "");
  if (!digits) return "#";
  const withCountry = digits.startsWith("55") ? digits : `55${digits}`;
  let rendered = msg || "";
  const ctx = client.ctx || {};
  Object.keys(ctx).forEach((k) => {
    const v = ctx[k] == null ? "" : String(ctx[k]);
    rendered = rendered.replaceAll(`{${k}}`, v).replaceAll(`{{${k}}}`, v);
  });
  return `https://wa.me/${withCountry}?text=${encodeURIComponent(rendered)}`;
}


const emptyForm = {
  segment_id: null,
  name: "",
  description: "",
  icon: "🎯",
  conditions: [],
  default_message: "Olá {nome}! 💜",
  enabled: true,
  channel: "whatsapp_zapi",
  unit_id: null,
};

export default function Segments() {
  const { apiCall, user } = useAuth();
  const { units } = useUnits();
  const activeUnits = (units || []).filter((u) => u.active !== false);
  const isMaster = user?.role === "master";
  const canDispatch = isMaster || Boolean(user?.can_manual_dispatch);

  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Editor Master
  const [editorOpen, setEditorOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Preview & Dispatch
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [currentSegment, setCurrentSegment] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [message, setMessage] = useState("");
  const [channelOverride, setChannelOverride] = useState(null);
  const [searchClient, setSearchClient] = useState("");
  const [dispatching, setDispatching] = useState(false);

  // Run tracker
  const [runOpen, setRunOpen] = useState(false);
  const [runId, setRunId] = useState(null);
  const [runData, setRunData] = useState(null);
  const pollRef = useRef(null);

  // Reatribuição de unidade em lote a partir da listagem do segmento
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignSel, setReassignSel] = useState(new Set());
  const [reassignTargetUnit, setReassignTargetUnit] = useState("");
  const [reassignReason, setReassignReason] = useState("");
  const [reassignBusy, setReassignBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiCall("get", "/segments");
      setSegments(r.data || []);
    } catch {
      toast.error("Erro ao carregar segmentos");
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => { load(); }, [load]);

  // ==== Master editor ====
  const openCreate = () => {
    // iter53 UX: se salão tem só 1 unidade ativa, pré-seleciona automaticamente
    // para reduzir fricção. Se >1, exige escolha explícita.
    const defaultUnitId = activeUnits.length === 1 ? activeUnits[0].unit_id : null;
    setForm({ ...emptyForm, unit_id: defaultUnitId });
    setEditorOpen(true);
  };
  const openEdit = (seg) => {
    // iter53 UX: segmentos legados sem unit_id ganham auto-preenchimento
    // quando só existe 1 unidade — mesma regra do openCreate.
    const fallbackUnit = !seg.unit_id && activeUnits.length === 1 ? activeUnits[0].unit_id : (seg.unit_id || null);
    setForm({
      segment_id: seg.segment_id,
      name: seg.name || "",
      description: seg.description || "",
      icon: seg.icon || "🎯",
      conditions: seg.conditions || [],
      default_message: seg.default_message || "",
      enabled: seg.enabled !== false,
      channel: normalizeChannel(seg.channel),
      unit_id: fallbackUnit,
    });
    setEditorOpen(true);
  };
  const saveForm = async () => {
    if (!form.name.trim()) { toast.error("Dê um nome ao segmento"); return; }
    if (form.enabled && (!form.conditions || form.conditions.length === 0)) {
      toast.error("Adicione pelo menos 1 regra ou desmarque 'Ativo' antes de salvar.");
      return;
    }
    const unknownVars = findUnknownVars(form.default_message);
    if (unknownVars.length > 0) {
      toast.error(`Mensagem tem variáveis não suportadas: ${unknownVars.map(v => "{" + v + "}").join(", ")}`);
      return;
    }
    if (!form.unit_id) {
      toast.error("Selecione a unidade do segmento. Sem unidade não é possível disparar.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        description: form.description || "",
        icon: form.icon || "🎯",
        conditions: form.conditions || [],
        default_message: form.default_message || "",
        enabled: !!form.enabled,
        channel: normalizeChannel(form.channel),
        unit_id: form.unit_id,
      };
      if (form.segment_id) {
        await apiCall("put", `/segments/${form.segment_id}`, body);
        toast.success("Segmento atualizado");
      } else {
        await apiCall("post", "/segments", body);
        toast.success("Segmento criado");
      }
      setEditorOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const duplicate = async (seg) => {
    try {
      await apiCall("post", `/segments/${seg.segment_id}/duplicate`);
      toast.success("Cópia criada (desativada). Clique em Editar para revisar.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao duplicar");
    }
  };

  const remove = async (seg) => {
    if (seg.is_system) {
      if (!window.confirm(`Não é possível excluir segmentos do sistema. Deseja apenas DESATIVAR "${seg.name}"?`)) return;
    } else {
      if (!window.confirm(`Excluir definitivamente o segmento "${seg.name}"?`)) return;
    }
    setDeletingId(seg.segment_id);
    try {
      await apiCall("delete", `/segments/${seg.segment_id}`);
      toast.success(seg.is_system ? "Segmento desativado" : "Segmento excluído");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir");
    } finally {
      setDeletingId(null);
    }
  };

  const toggleEnabled = async (seg) => {
    if (!isMaster) return;
    try {
      await apiCall("put", `/segments/${seg.segment_id}`, {
        name: seg.name,
        description: seg.description,
        icon: seg.icon,
        conditions: seg.conditions,
        default_message: seg.default_message,
        enabled: !seg.enabled,
        unit_id: seg.unit_id,
        channel: normalizeChannel(seg.channel),
      });
      load();
    } catch {
      toast.error("Erro ao atualizar");
    }
  };

  // ==== Preview & Dispatch ====
  const openPreview = async (seg) => {
    setCurrentSegment(seg);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewData(null);
    setSelected(new Set());
    setMessage(seg.default_message || "");
    setChannelOverride(normalizeChannel(seg.channel));
    setSearchClient("");
    try {
      const r = await apiCall("get", `/segments/${seg.segment_id}/preview`);
      setPreviewData(r.data);
      // Auto-selecionar apenas clientes aptos: com telefone, não duplicados
      // e que NÃO receberam a MESMA msg nas últimas 24h (iter62)
      const eligibles = new Set(
        (r.data.matched || [])
          .filter(c => c.has_phone && !c.is_duplicate && !c.already_received_same_msg)
          .map(c => c.client_id)
      );
      setSelected(eligibles);
      setShowRecent(false); // reset accordion do grupo "já receberam"
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro no preview");
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const filteredClients = useMemo(() => {
    const q = (searchClient || "").toLowerCase().trim();
    const list = previewData?.matched || [];
    if (!q) return list;
    return list.filter(c =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.phone_masked || "").toLowerCase().includes(q)
    );
  }, [searchClient, previewData]);

  // 🎯 iter62: Split visual — separa quem já recebeu a MESMA msg em 24h dos novos destinatários
  const filteredFresh = useMemo(
    () => filteredClients.filter(c => !c.already_received_same_msg),
    [filteredClients]
  );
  const filteredRecent = useMemo(
    () => filteredClients.filter(c => c.already_received_same_msg),
    [filteredClients]
  );
  const [showRecent, setShowRecent] = useState(false);

  const toggleSel = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };

  const toggleAll = () => {
    // "Selecionar todos" opera SÓ sobre novos destinatários (não marca os já-receberam por padrão)
    const selectable = filteredFresh.filter(c => c.has_phone && !c.is_duplicate);
    if (selectable.length > 0 && selectable.every(c => selected.has(c.client_id))) {
      const s = new Set(selected);
      selectable.forEach(c => s.delete(c.client_id));
      setSelected(s);
    } else {
      const s = new Set(selected);
      selectable.forEach(c => s.add(c.client_id));
      setSelected(s);
    }
  };

  const doDispatch = async () => {
    if (!canDispatch) { toast.error("Você não tem permissão para disparar."); return; }
    if (!message.trim()) { toast.error("Preencha a mensagem"); return; }
    if (selected.size === 0) { toast.error("Selecione ao menos 1 cliente"); return; }
    // 🎯 Bloqueio de variáveis desconhecidas — não deixa {xpto} chegar no cliente
    const unknownVars = findUnknownVars(message);
    if (unknownVars.length > 0) {
      toast.error(`Variáveis não suportadas na mensagem: ${unknownVars.map(v => "{" + v + "}").join(", ")}`);
      return;
    }
    if (!window.confirm(`Disparar para ${selected.size} cliente(s) via ${channelLabel(channelOverride)}?`)) return;
    setDispatching(true);
    try {
      const r = await apiCall("post", `/segments/${currentSegment.segment_id}/dispatch`, {
        client_ids: Array.from(selected),
        message: message.trim(),
        channel: normalizeChannel(channelOverride),
      });
      toast.success(r.data?.message || "Disparo iniciado");
      setPreviewOpen(false);
      // Abre tracker
      if (r.data?.run_id) {
        setRunId(r.data.run_id);
        setRunOpen(true);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao disparar");
    } finally {
      setDispatching(false);
    }
  };

  // ==== Run polling ====
  useEffect(() => {
    if (!runOpen || !runId) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    const fetchRun = async () => {
      try {
        const r = await apiCall("get", `/segments/runs/${runId}`);
        setRunData(r.data);
        if (r.data?.status === "ok" || r.data?.finished_at) {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        }
      } catch (e) {
        // silent
      }
    };
    fetchRun();
    pollRef.current = setInterval(fetchRun, 2500);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [runOpen, runId, apiCall]);

  const channelLabel = (v) => CHANNELS.find(c => c.value === v)?.label || v;

  // ==== Reatribuição de unidade em lote ====
  useEffect(() => {
    if (reassignOpen) {
      // Seed com IDs já selecionados na tela de disparo; se vazio, seleciona todos
      const seed = selected.size > 0 ? new Set(selected) : new Set(filteredClients.map(c => c.client_id));
      setReassignSel(seed);
      setReassignTargetUnit("");
      setReassignReason("");
    }
  }, [reassignOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleReassignSel = (id) => {
    const s = new Set(reassignSel);
    if (s.has(id)) s.delete(id); else s.add(id);
    setReassignSel(s);
  };
  const toggleReassignAll = () => {
    if (filteredClients.length > 0 && filteredClients.every(c => reassignSel.has(c.client_id))) {
      const s = new Set(reassignSel);
      filteredClients.forEach(c => s.delete(c.client_id));
      setReassignSel(s);
    } else {
      const s = new Set(reassignSel);
      filteredClients.forEach(c => s.add(c.client_id));
      setReassignSel(s);
    }
  };

  const doBulkReassign = async () => {
    if (!reassignTargetUnit) { toast.error("Selecione a unidade destino"); return; }
    if (reassignSel.size === 0) { toast.error("Selecione ao menos 1 cliente"); return; }
    const unitName = activeUnits.find(u => u.unit_id === reassignTargetUnit)?.name || reassignTargetUnit;
    if (!window.confirm(`Mover ${reassignSel.size} cliente(s) para "${unitName}"? Ação reversível via run_id.`)) return;
    setReassignBusy(true);
    try {
      const r = await apiCall("post", "/clients/bulk-reassign-unit", {
        client_ids: Array.from(reassignSel),
        new_unit_id: reassignTargetUnit,
        reason: reassignReason.trim() || null,
      });
      const d = r.data || {};
      toast.success(
        `Movidos: ${d.modified || 0} · já estavam nesta unidade: ${d.skipped_same_unit || 0} · não encontrados: ${d.not_found || 0}` +
        (d.run_id ? ` · rollback: ${d.run_id}` : "")
      );
      setReassignOpen(false);
      // Recarrega preview para refletir novas unidades
      if (currentSegment?.segment_id) {
        try {
          const pr = await apiCall("get", `/segments/${currentSegment.segment_id}/preview`);
          setPreviewData(pr.data);
        } catch { /* preview reload é best-effort */ }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao trocar unidades");
    } finally {
      setReassignBusy(false);
    }
  };

  if (loading) return (
    <div className="p-8 text-center text-muted-foreground">
      <Loader2 className="h-6 w-6 animate-spin mx-auto" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="segments-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-4xl font-light tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Segmentos Inteligentes
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isMaster
              ? "Crie e mantenha listas prontas de clientes. A recepção só clica em \"Ver clientes\" e dispara."
              : canDispatch
                ? "Escolha um segmento, revise a lista e dispare. Sem editar filtros."
                : "Você pode consultar prévias, mas não tem permissão para disparar. Peça ao master para habilitar."}
          </p>
        </div>
        {isMaster && (
          <Button onClick={openCreate} data-testid="new-segment-btn" className="gap-2">
            <Plus className="h-4 w-4" strokeWidth={1.5} />
            Novo segmento
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {segments.filter(s => isMaster || s.enabled).map((seg) => (
          <Card
            key={seg.segment_id}
            className={`p-5 shadow-soft hover:shadow-md transition-shadow ${!seg.enabled ? "opacity-60" : ""}`}
            data-testid={`segment-card-${seg.segment_id}`}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="text-3xl">{seg.icon}</div>
              <div className="flex gap-1 flex-wrap justify-end">
                {seg.is_system && <Badge variant="outline" className="text-[10px]">sistema</Badge>}
                {!seg.enabled && <Badge className="bg-gray-200 text-gray-700 text-[10px]">inativo</Badge>}
                {seg.unit_id ? (
                  <Badge variant="outline" className="text-[10px] border-emerald-200 text-emerald-700 bg-emerald-50" title="Unidade do segmento">
                    {(activeUnits.find((u) => u.unit_id === seg.unit_id)?.name) || seg.unit_id}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50" title="Segmento sem unidade — edite antes de disparar">
                    sem unidade
                  </Badge>
                )}
                <Badge variant="outline" className="text-[10px]">{channelLabel(seg.channel || "whatsapp_zapi")}</Badge>
              </div>
            </div>
            <div className="font-medium text-base leading-tight mb-1">{seg.name}</div>
            <div className="text-xs text-muted-foreground line-clamp-2 mb-3 min-h-[32px]">{seg.description}</div>
            <div className="flex gap-1.5 flex-wrap">
              <Button
                size="sm"
                variant="outline"
                onClick={() => openPreview(seg)}
                disabled={!seg.enabled && !isMaster}
                data-testid={`open-preview-${seg.segment_id}`}
                className="text-xs"
              >
                <Eye className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />
                Ver clientes
              </Button>
              {isMaster && (
                <>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openEdit(seg)}
                    title="Editar"
                    data-testid={`edit-${seg.segment_id}`}
                    className="text-xs"
                  >
                    <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => duplicate(seg)}
                    title="Duplicar"
                    data-testid={`duplicate-${seg.segment_id}`}
                    className="text-xs"
                  >
                    <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => toggleEnabled(seg)}
                    title={seg.enabled ? "Desativar" : "Ativar"}
                    data-testid={`toggle-${seg.segment_id}`}
                    className="text-xs"
                  >
                    <Power className={`h-3.5 w-3.5 ${seg.enabled ? "text-green-600" : "text-gray-400"}`} strokeWidth={1.5} />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => remove(seg)}
                    disabled={deletingId === seg.segment_id}
                    title={seg.is_system ? "Desativar (não é possível excluir do sistema)" : "Excluir"}
                    data-testid={`delete-${seg.segment_id}`}
                    className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </Button>
                </>
              )}
            </div>
          </Card>
        ))}
      </div>

      {segments.length === 0 && (
        <Card className="p-12 text-center" data-testid="empty-state">
          <Target className="h-10 w-10 mx-auto mb-2 text-muted-foreground" strokeWidth={1.5} />
          <p className="text-sm text-muted-foreground mb-4">Nenhum segmento cadastrado.</p>
          {isMaster && (
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <Button
                onClick={async () => {
                  try {
                    const r = await apiCall("post", "/segments/seed-defaults");
                    toast.success(`${r.data?.system_segments_count || 0} segmentos padrão criados`);
                    load();
                  } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
                }}
                variant="outline"
                data-testid="seed-defaults-btn"
              >
                🌱 Criar 7 modelos padrão
              </Button>
              <Button onClick={openCreate} className="gap-2" data-testid="empty-new-btn">
                <Plus className="h-4 w-4" strokeWidth={1.5} /> Criar meu primeiro segmento
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* ==================== MASTER EDITOR ==================== */}
      <Dialog open={editorOpen} onOpenChange={(v) => !v && !saving && setEditorOpen(false)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="segment-editor">
          <DialogHeader>
            <DialogTitle className="text-2xl font-light flex items-center gap-2">
              <span className="text-3xl">{form.icon}</span>
              {form.segment_id ? "Editar segmento" : "Novo segmento"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Escolha quem deve entrar neste grupo. A recepção só clica em &quot;Ver clientes&quot; e dispara — sem editar filtros.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
              <div className="sm:col-span-1">
                <Label className="text-xs">Ícone</Label>
                <Input
                  value={form.icon}
                  onChange={(e) => setForm({ ...form, icon: e.target.value })}
                  placeholder="🎯"
                  className="text-center text-xl"
                  data-testid="form-icon"
                  maxLength={4}
                />
              </div>
              <div className="sm:col-span-3">
                <Label className="text-xs">Nome *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ex: Aniversariantes de julho"
                  data-testid="form-name"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">
                  Unidade <span className="text-destructive">*</span>
                </Label>
                <select
                  value={form.unit_id || ""}
                  onChange={(e) => setForm({ ...form, unit_id: e.target.value || null })}
                  className="h-9 w-full px-2 text-xs border border-input rounded-md bg-background"
                  data-testid="form-unit-id"
                >
                  <option value="">— Selecione a unidade —</option>
                  {activeUnits.map((u) => (
                    <option key={u.unit_id} value={u.unit_id}>{u.name}</option>
                  ))}
                </select>
                <div className="text-[10px] text-muted-foreground mt-1">
                  O segmento e todos os disparos ficarão isolados nesta unidade — clientes de outras unidades não recebem.
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Canal de envio</Label>
                <select
                  value={form.channel}
                  onChange={(e) => setForm({ ...form, channel: e.target.value })}
                  className="h-9 w-full px-2 text-xs border border-input rounded-md bg-background"
                  data-testid="form-channel"
                >
                  {CHANNELS.map(c => (
                    <option key={c.value} value={c.value}>
                      {c.label} {c.status !== "ativo" ? `(${c.status})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label className="text-xs">Descrição (aparece no card)</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Ex: Clientes que aniversariam este mês"
                data-testid="form-description"
              />
            </div>

            <div>
              <Label className="text-xs">Quem entra neste segmento</Label>
              <p className="text-[10px] text-muted-foreground mb-1.5">
                Adicione pelo menos uma regra para evitar envio para toda a base.
              </p>
              <ConditionsBuilder
                value={form.conditions}
                onChange={(c) => setForm({ ...form, conditions: c })}
              />
              {form.enabled && (!form.conditions || form.conditions.length === 0) && (
                <div className="text-[11px] text-red-700 mt-1.5">
                  ⚠ Segmento ativo sem regras será bloqueado no salvar. Adicione ao menos 1 regra.
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs">Mensagem padrão</Label>
              <Textarea
                rows={3}
                value={form.default_message}
                onChange={(e) => setForm({ ...form, default_message: e.target.value })}
                placeholder="Oi {primeiro_nome}, tudo bem?"
                data-testid="form-message"
              />
              <VariablePalette
                onInsert={(placeholder) => setForm(f => ({ ...f, default_message: (f.default_message || "") + " " + placeholder }))}
                template={form.default_message}
                testIdPrefix="editor"
              />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="form-enabled"
                checked={!!form.enabled}
                onCheckedChange={(v) => setForm({ ...form, enabled: !!v })}
                data-testid="form-enabled"
              />
              <label htmlFor="form-enabled" className="text-xs cursor-pointer">
                Ativo — operadores veem este segmento e podem disparar
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)} disabled={saving} data-testid="editor-cancel">
              Cancelar
            </Button>
            <Button
              onClick={saveForm}
              disabled={
                saving ||
                !form.name.trim() ||
                (form.enabled && (!form.conditions || form.conditions.length === 0)) ||
                findUnknownVars(form.default_message).length > 0
              }
              data-testid="editor-save"
            >
              {saving ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Salvando…</> : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== PREVIEW & DISPATCH ==================== */}
      <Dialog open={previewOpen} onOpenChange={(v) => !v && !dispatching && setPreviewOpen(false)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="segment-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl font-light">
              <span className="text-3xl">{currentSegment?.icon}</span>
              {currentSegment?.name}
            </DialogTitle>
            <DialogDescription className="text-xs">{currentSegment?.description}</DialogDescription>
          </DialogHeader>

          {previewLoading ? (
            <div className="py-12 text-center text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
              Buscando clientes...
            </div>
          ) : previewData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Card className="p-3">
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Base</div>
                  <div className="text-xl font-light">{previewData.total_scanned}</div>
                </Card>
                <Card className="p-3 bg-emerald-50 border-emerald-200">
                  <div className="text-[10px] text-emerald-800 uppercase tracking-wider">Encontrados</div>
                  <div className="text-xl font-light text-emerald-700">{previewData.matched_count}</div>
                </Card>
                <Card className="p-3 bg-sky-50 border-sky-200">
                  <div className="text-[10px] text-sky-800 uppercase tracking-wider">Aptos ao envio</div>
                  <div className="text-xl font-light text-sky-700">{previewData.eligible_count}</div>
                  <div className="text-[9px] text-sky-700 mt-0.5">= telefones únicos</div>
                </Card>
                <Card className="p-3 bg-amber-50 border-amber-200">
                  <div className="text-[10px] text-amber-800 uppercase tracking-wider">Sem tel / Duplicados</div>
                  <div className="text-xl font-light text-amber-700">
                    {previewData.no_phone_count} <span className="text-xs">/</span> {previewData.duplicate_count || 0}
                  </div>
                </Card>
              </div>

              {(previewData.duplicate_count || 0) > 0 && (
                <Card className="p-3 bg-orange-50 border-orange-200">
                  <div className="flex items-start gap-2 text-xs text-orange-900">
                    <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <div>
                      <div className="font-medium mb-0.5">
                        {previewData.duplicate_count} cadastro(s) com telefone já usado por outro cliente do segmento.
                      </div>
                      Cada telefone recebe no máximo uma mensagem. Os cadastros duplicados aparecem marcados abaixo e serão automaticamente ignorados.
                    </div>
                  </div>
                </Card>
              )}

              {previewData.matched_count === 0 && previewData.diagnostics && (
                <Card className="p-4 bg-amber-50 border-amber-200 space-y-2" data-testid="zero-diagnostics">
                  <div className="flex items-start gap-2 text-xs text-amber-900">
                    <AlertTriangle className="h-4 w-4 mt-0.5" strokeWidth={1.5} />
                    <div className="flex-1">
                      <div className="font-medium mb-1">Nenhum cliente casou com os filtros.</div>
                      <div>Base escaneada: <b>{previewData.diagnostics.base_total}</b> clientes. Análise:</div>
                    </div>
                  </div>
                  {previewData.diagnostics.conditions_analysis && previewData.diagnostics.conditions_analysis.length > 0 && (
                    <div className="text-[11px] pl-6 space-y-0.5">
                      {previewData.diagnostics.conditions_analysis.map((c, i) => (
                        <div key={i} className="flex justify-between border-b border-amber-100 py-1">
                          <span>{c.label} <code className="text-[10px] opacity-60">{c.op}</code> {String(c.value ?? "")}</span>
                          <span className="text-amber-800 font-medium">
                            passa {c.passing} · exclui {c.excluded}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {previewData.diagnostics.tips && previewData.diagnostics.tips.length > 0 && (
                    <div className="pl-6 text-[11px] text-amber-900">
                      💡 {previewData.diagnostics.tips.join(" ")}
                    </div>
                  )}
                </Card>
              )}

              {canDispatch && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-2">
                    <Label className="text-xs">Mensagem</Label>
                    <Textarea
                      rows={3}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Oi {primeiro_nome}, tudo bem?"
                      data-testid="segment-message"
                    />
                    <VariablePalette
                      onInsert={(placeholder) => setMessage(m => (m || "") + " " + placeholder)}
                      template={message}
                      testIdPrefix="dispatch"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Canal</Label>
                    <select
                      value={channelOverride || "whatsapp_zapi"}
                      onChange={(e) => setChannelOverride(e.target.value)}
                      className="h-9 w-full px-2 text-xs border border-input rounded-md bg-background"
                      data-testid="dispatch-channel"
                    >
                      {CHANNELS.map(c => (
                        <option key={c.value} value={c.value}>
                          {c.label} {c.status !== "ativo" ? `(${c.status})` : ""}
                        </option>
                      ))}
                    </select>
                    {channelOverride === "whatsapp_manual" && (
                      <div className="text-[11px] text-sky-800 mt-1.5 bg-sky-50 border border-sky-200 rounded-md p-2 leading-relaxed">
                        <div className="font-medium mb-0.5">🔗 Modo manual — sistema NÃO envia sozinho</div>
                        <ol className="list-decimal list-inside space-y-0.5 mt-1">
                          <li>Cada cliente ganha um botão <b>Abrir WhatsApp</b> na lista abaixo — clique para abrir a conversa com a mensagem já preenchida.</li>
                          <li>Ou clique em <b>Copiar todos os links</b> pra colar em outro lugar.</li>
                          <li>O botão verde <b>&quot;Disparar&quot;</b> abaixo é opcional — ele só REGISTRA no histórico que você planeja enviar (não abre WhatsApp nem envia).</li>
                        </ol>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Prévia da mensagem renderizada — usa ctx do backend + renderiza CURRENT message */}
              {previewData.matched_count > 0 && (() => {
                const first = previewData.matched[0];
                const ctx = first?.ctx || {};
                let renderedLive = message || "";
                let missingLive = [];
                TEMPLATE_VARS.forEach(v => {
                  const val = ctx[v.key] || "";
                  const hasVar = new RegExp(`\\{\\{?\\s*${v.key}\\s*\\}?\\}`).test(message);
                  if (hasVar && !val) missingLive.push(v.key);
                  renderedLive = renderedLive
                    .replaceAll(`{${v.key}}`, String(val))
                    .replaceAll(`{{${v.key}}}`, String(val));
                });
                const unknownLive = findUnknownVars(message);
                return (
                  <Card className="p-3 bg-slate-50 border-dashed">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                      Exemplo — como o cliente vai receber
                    </div>
                    <pre className="text-xs whitespace-pre-wrap font-sans" data-testid="rendered-example">
                      {renderedLive}
                    </pre>
                    <div className="text-[10px] text-muted-foreground mt-1">
                      Para: <b>{first?.name}</b> · {first?.phone_masked}
                    </div>
                    {missingLive.length > 0 && (
                      <div className="text-[10px] text-amber-800 mt-2 border-t border-amber-200 pt-1.5" data-testid="missing-vars-warning">
                        ⚠ Este cliente não tem valor para: {missingLive.map(v => `{${v}}`).join(", ")}. A mensagem vai sair com esses trechos em branco.
                      </div>
                    )}
                    {unknownLive.length > 0 && (
                      <div className="text-[10px] text-red-700 mt-2 border-t border-red-200 pt-1.5" data-testid="unknown-vars-warning">
                        🚫 Variáveis não suportadas: {unknownLive.map(v => `{${v}}`).join(", ")}. Envio bloqueado até corrigir.
                      </div>
                    )}
                  </Card>
                );
              })()}

              <div>
                <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                    <Search className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
                    <Input
                      value={searchClient}
                      onChange={(e) => setSearchClient(e.target.value)}
                      placeholder="Buscar cliente..."
                      className="h-8 text-xs"
                      data-testid="preview-search"
                    />
                  </div>
                  {channelOverride === "whatsapp_manual" && filteredClients.some(c => c.has_phone && !c.is_duplicate) && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs border-sky-300 text-sky-700 hover:bg-sky-50"
                      data-testid="copy-all-wa-preview"
                      onClick={() => {
                        const lines = filteredClients
                          .filter(c => c.has_phone && !c.is_duplicate)
                          .map(c => `${c.name || c.client_id}: ${waLinkFor(c, message)}`)
                          .join("\n");
                        if (lines) {
                          navigator.clipboard.writeText(lines);
                          toast.success(`${lines.split("\n").length} link(s) copiado(s)`);
                        }
                      }}
                    >
                      🔗 Copiar todos os links
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 text-xs border-amber-300 text-amber-800 hover:bg-amber-50"
                    onClick={() => setReassignOpen(true)}
                    disabled={filteredClients.length === 0}
                    data-testid="open-bulk-reassign-btn"
                    title="Trocar em lote a unidade de clientes desta lista"
                  >
                    Trocar unidade…
                  </Button>
                  <button
                    type="button"
                    onClick={toggleAll}
                    data-testid="toggle-all-btn"
                    className="inline-flex items-center gap-1.5 h-8 px-3 text-xs border border-input bg-background hover:bg-accent rounded-md"
                  >
                    <span className={`inline-flex items-center justify-center h-3.5 w-3.5 rounded border ${filteredClients.length > 0 && filteredClients.every(c => selected.has(c.client_id)) ? "bg-primary border-primary text-primary-foreground" : "border-input"}`}>
                      {filteredClients.length > 0 && filteredClients.every(c => selected.has(c.client_id)) && (
                        <CheckCircle2 className="h-2.5 w-2.5" strokeWidth={3} />
                      )}
                    </span>
                    Selecionar todos
                  </button>
                </div>
                <div className="border rounded-md max-h-72 overflow-y-auto">
                  {filteredClients.length === 0 ? (
                    <div className="p-6 text-center text-xs text-muted-foreground">Nenhum cliente encontrado</div>
                  ) : (
                    <>
                      {/* Grupo 1: Novos destinatários (não receberam a MESMA msg nas últimas 24h) */}
                      {filteredFresh.length > 0 && (
                        <div className="bg-emerald-50/40 border-b border-emerald-200 px-2 py-1 text-[10px] font-semibold text-emerald-800 sticky top-0 z-10" data-testid="fresh-group-header">
                          ✅ Novos destinatários — {filteredFresh.length} · marcados por padrão
                        </div>
                      )}
                      {filteredFresh.map((c) => (
                        <ClientRow
                          key={c.client_id}
                          c={c}
                          selected={selected}
                          toggleSel={toggleSel}
                          channelOverride={channelOverride}
                          waLinkFor={waLinkFor}
                          message={message}
                        />
                      ))}
                      {/* Grupo 2: Já receberam a MESMA msg em 24h (agrupados, desmarcados) */}
                      {filteredRecent.length > 0 && (
                        <>
                          <button
                            type="button"
                            onClick={() => setShowRecent(v => !v)}
                            className="w-full flex items-center justify-between bg-amber-50 border-b border-amber-200 hover:bg-amber-100 px-2 py-1.5 text-[10px] font-semibold text-amber-900 sticky top-0 z-10"
                            data-testid="recent-group-toggle"
                          >
                            <span>⚠ Já receberam esta MESMA mensagem nas últimas {previewData?.dedupe_hours || 24}h — {filteredRecent.length}</span>
                            <span>{showRecent ? "esconder ▲" : "revisar / autorizar reenvio ▼"}</span>
                          </button>
                          {showRecent && filteredRecent.map((c) => (
                            <ClientRow
                              key={c.client_id}
                              c={c}
                              selected={selected}
                              toggleSel={toggleSel}
                              channelOverride={channelOverride}
                              waLinkFor={waLinkFor}
                              message={message}
                              isRecent={true}
                            />
                          ))}
                        </>
                      )}
                    </>
                  )}
                </div>
                <div className="text-[10px] text-muted-foreground mt-1 text-right">
                  {selected.size} selecionado(s) · {filteredFresh.length} novos · {filteredRecent.length} já receberam · {previewData.matched_count} no total
                  {previewData?.matched_truncated && (
                    <span className="ml-2 text-amber-700 font-medium" data-testid="matched-truncated-warning">
                      ⚠ Mostrando 5.000 de {previewData.matched_total} — refine os filtros para ver o restante
                    </span>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setPreviewOpen(false)} disabled={dispatching}>
              {channelOverride === "whatsapp_manual" ? "Concluir" : "Fechar"}
            </Button>
            {canDispatch && (
              <Button
                size="sm"
                onClick={doDispatch}
                disabled={
                  dispatching ||
                  previewLoading ||
                  selected.size === 0 ||
                  !message.trim() ||
                  previewData?.matched_count === 0 ||
                  findUnknownVars(message).length > 0
                }
                className={channelOverride === "whatsapp_manual" ? "bg-sky-600 hover:bg-sky-700 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
                data-testid="dispatch-btn"
                title={channelOverride === "whatsapp_manual" ? "Opcional: apenas REGISTRA no histórico (Runs) que você planeja enviar. Não abre WhatsApp." : ""}
              >
                {dispatching ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Enviando...</> : channelOverride === "whatsapp_manual" ? (
                  <><ClipboardCheck className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> Registrar {selected.size} no histórico</>
                ) : (
                  <><Send className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> Disparar para {selected.size}</>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== RUN TRACKER ==================== */}
      <Dialog open={runOpen} onOpenChange={setRunOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="run-tracker-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-light">Resultado do disparo</DialogTitle>
            <DialogDescription className="text-xs">
              Execução <code>{runId}</code> · {runData?.channel || "—"}
              {runData?.unit_label && (
                <span className="ml-2 inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="run-unit-label">
                  {runData.unit_label}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {!runData ? (
            <div className="py-6 text-center text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
              Aguardando início...
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-2">
                <Card className="p-3">
                  <div className="text-[10px] uppercase text-muted-foreground">Solicitados</div>
                  <div className="text-xl font-light">{runData.requested}</div>
                </Card>
                <Card className="p-3 bg-emerald-50 border-emerald-200" data-testid="counter-accepted">
                  <div className="text-[10px] uppercase text-emerald-800">Aceitos pela Z-API</div>
                  <div className="text-xl font-light text-emerald-700">
                    {runData.counters?.real_sends ?? ((runData.counters?.accepted || 0) + (runData.counters?.sent || 0))}
                  </div>
                </Card>
                <Card className="p-3 bg-sky-50 border-sky-200">
                  <div className="text-[10px] uppercase text-sky-800">Manuais prontos</div>
                  <div className="text-xl font-light text-sky-700">{runData.counters?.manual_ready || 0}</div>
                </Card>
                <Card className="p-3 bg-amber-50 border-amber-200">
                  <div className="text-[10px] uppercase text-amber-800">Ignorados</div>
                  <div className="text-xl font-light text-amber-700">{runData.counters?.skipped || 0}</div>
                </Card>
                <Card className="p-3 bg-amber-50 border-amber-300" data-testid="counter-unknown">
                  <div className="text-[10px] uppercase text-amber-900">Sem confirmação</div>
                  <div className="text-xl font-light text-amber-800">
                    {runData.counters?.pending_confirmation ?? ((runData.counters?.unknown || 0) + (runData.counters?.queued || 0) + (runData.counters?.sending || 0))}
                  </div>
                </Card>
                <Card className="p-3 bg-rose-50 border-rose-200">
                  <div className="text-[10px] uppercase text-rose-800">Erros</div>
                  <div className="text-xl font-light text-rose-700">{runData.counters?.failed || 0}</div>
                </Card>
              </div>

              {(runData.counters?.manual_ready || 0) > 0 && (
                <div className="rounded-md border border-sky-200 bg-sky-50 p-3 space-y-2" data-testid="manual-links-panel">
                  <div className="text-xs font-medium text-sky-800">
                    🔗 {runData.counters.manual_ready} link(s) wa.me pronto(s) — clique para abrir ou copie tudo.
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-[11px]"
                      data-testid="copy-all-wa-links"
                      onClick={() => {
                        const lines = (runData.sends || [])
                          .filter(s => s.status === "manual_ready" && s.wa_link)
                          .map(s => `${s.client_name || s.client_id}: ${s.wa_link}`)
                          .join("\n");
                        if (lines) {
                          navigator.clipboard.writeText(lines);
                          toast.success("Links copiados para a área de transferência");
                        } else {
                          toast.error("Nenhum link para copiar");
                        }
                      }}
                    >
                      Copiar todos os links
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2 text-xs">
                {runData.finished_at ? (
                  <><CheckCircle2 className="h-4 w-4 text-emerald-600" strokeWidth={1.5} /> <span>Execução finalizada</span></>
                ) : (
                  <><Loader2 className="h-4 w-4 animate-spin" /> <span>Executando em background — pode fechar essa janela.</span></>
                )}
                {runData.counters?.queued > 0 && !runData.finished_at && (
                  <Badge variant="outline">{runData.counters.queued} na fila</Badge>
                )}
              </div>

              <div className="border rounded-md max-h-64 overflow-y-auto">
                {(runData.sends || []).length === 0 ? (
                  <div className="p-4 text-center text-xs text-muted-foreground">Nenhum envio ainda</div>
                ) : (runData.sends || []).map((s) => (
                  <div key={s.client_id} className="flex items-center gap-2 p-2 border-b last:border-0 text-xs">
                    <StatusIcon status={s.status} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{s.client_name || s.client_id}</div>
                      <div className="text-[10px] text-muted-foreground">
                        {s.phone_masked || "—"}
                        {s.skip_reason_label && ` · ${s.skip_reason_label}`}
                        {!s.skip_reason_label && s.skip_reason && ` · ${s.skip_reason}`}
                        {s.last_error && s.status === "failed" && ` · ${s.last_error}`}
                      </div>
                    </div>
                    {s.status === "manual_ready" && s.wa_link ? (
                      <a
                        href={s.wa_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[10px] px-2 py-0.5 rounded bg-sky-600 text-white hover:bg-sky-700"
                        data-testid={`wa-link-${s.client_id}`}
                      >
                        Abrir WhatsApp
                      </a>
                    ) : (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${statusColor(s.status)}`}>{statusLabel(s.status)}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRunOpen(false)} data-testid="close-run-tracker">Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Bulk Reassign Unit Modal ===== */}
      <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="bulk-reassign-dialog">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-2xl">
              Trocar unidade em lote
            </DialogTitle>
            <DialogDescription>
              Move os clientes selecionados para outra unidade. Ação reversível — cada execução gera um <code>run_id</code> para rollback.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest">Nova unidade *</Label>
                <select
                  value={reassignTargetUnit}
                  onChange={(e) => setReassignTargetUnit(e.target.value)}
                  className="h-9 w-full mt-1 border rounded-md px-2 text-sm bg-background"
                  data-testid="bulk-reassign-unit-select"
                >
                  <option value="">— Selecione —</option>
                  {activeUnits.map((u) => (
                    <option key={u.unit_id} value={u.unit_id}>{u.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Motivo (opcional)</Label>
                <Input
                  value={reassignReason}
                  onChange={(e) => setReassignReason(e.target.value)}
                  placeholder="Ex: correção de vínculo por atendimento"
                  data-testid="bulk-reassign-reason"
                  className="h-9 mt-1"
                />
              </div>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">
                {reassignSel.size} de {filteredClients.length} selecionado(s)
              </span>
              <button
                type="button"
                onClick={toggleReassignAll}
                className="inline-flex items-center gap-1.5 h-7 px-2 border rounded hover:bg-accent"
                data-testid="bulk-reassign-toggle-all"
              >
                {filteredClients.length > 0 && filteredClients.every(c => reassignSel.has(c.client_id))
                  ? "Desmarcar todos"
                  : "Selecionar todos"}
              </button>
            </div>
            <div className="border rounded-md max-h-72 overflow-y-auto">
              {filteredClients.length === 0 ? (
                <div className="p-6 text-center text-xs text-muted-foreground">Nenhum cliente na listagem</div>
              ) : filteredClients.map((c) => {
                const curUnit = c.ctx?.unidade || "";
                return (
                  <label
                    key={c.client_id}
                    className="flex items-start gap-2 p-2 border-b last:border-0 hover:bg-accent/50 text-xs cursor-pointer"
                    data-testid={`bulk-reassign-row-${c.client_id}`}
                  >
                    <Checkbox
                      checked={reassignSel.has(c.client_id)}
                      onCheckedChange={() => toggleReassignSel(c.client_id)}
                      data-testid={`bulk-reassign-check-${c.client_id}`}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{c.name || "(sem nome)"}</div>
                      <div className="text-[10px] text-muted-foreground">
                        {c.has_phone ? c.phone_masked : "sem telefone"}
                        {curUnit ? ` · unidade atual: ${curUnit}` : ""}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReassignOpen(false)} data-testid="bulk-reassign-cancel">
              Cancelar
            </Button>
            <Button
              onClick={doBulkReassign}
              disabled={reassignBusy || !reassignTargetUnit || reassignSel.size === 0}
              data-testid="bulk-reassign-confirm"
            >
              {reassignBusy ? "Movendo…" : `Mover ${reassignSel.size} cliente(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusIcon({ status }) {
  // iter71: nova máquina de estados
  if (status === "accepted") return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" strokeWidth={1.5} />;
  if (status === "delivered") return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-700" strokeWidth={2} />;
  if (status === "read") return <CheckCircle2 className="h-3.5 w-3.5 text-blue-600" strokeWidth={2} />;
  if (status === "sent") return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" strokeWidth={1.5} />; // legacy
  if (status === "sending") return <Clock className="h-3.5 w-3.5 text-sky-600 animate-pulse" strokeWidth={1.5} />;
  if (status === "manual_ready") return <MessageSquare className="h-3.5 w-3.5 text-sky-600" strokeWidth={1.5} />;
  if (status === "failed") return <XCircle className="h-3.5 w-3.5 text-rose-600" strokeWidth={1.5} />;
  if (status === "unknown") return <AlertTriangle className="h-3.5 w-3.5 text-amber-500" strokeWidth={1.5} />;
  if (status === "skipped") return <AlertTriangle className="h-3.5 w-3.5 text-amber-600" strokeWidth={1.5} />;
  return <Clock className="h-3.5 w-3.5 text-slate-500" strokeWidth={1.5} />;
}

function statusColor(status) {
  // iter71: nova máquina de estados
  if (status === "accepted") return "bg-emerald-100 text-emerald-800";
  if (status === "delivered") return "bg-emerald-200 text-emerald-900";
  if (status === "read") return "bg-blue-100 text-blue-800";
  if (status === "sent") return "bg-emerald-50 text-emerald-700 border border-emerald-200"; // legacy: tom mais claro
  if (status === "sending") return "bg-sky-100 text-sky-800";
  if (status === "manual_ready") return "bg-sky-100 text-sky-800";
  if (status === "failed") return "bg-rose-100 text-rose-800";
  if (status === "unknown") return "bg-amber-100 text-amber-900 border border-amber-300";
  if (status === "skipped") return "bg-amber-50 text-amber-800";
  return "bg-slate-100 text-slate-700";
}

// iter71: rótulo legível
function statusLabel(status) {
  const m = {
    accepted: "Aceito pela Z-API",
    delivered: "Entregue",
    read: "Lida",
    sent: "Enviado (legado)",
    sending: "Enviando…",
    queued: "Na fila",
    manual_ready: "Link pronto",
    failed: "Falhou",
    unknown: "Sem confirmação",
    skipped: "Pulado",
  };
  return m[status] || status || "—";
}

// 🎯 Botões para inserir variáveis suportadas + validação inline de placeholders desconhecidos.
function VariablePalette({ onInsert, template, testIdPrefix }) {
  const unknown = findUnknownVars(template || "");
  return (
    <div className="mt-2 space-y-1.5">
      <div className="text-[10px] text-muted-foreground">Variáveis disponíveis (clique para inserir):</div>
      <div className="flex flex-wrap gap-1">
        {TEMPLATE_VARS.map((v) => (
          <button
            key={v.key}
            type="button"
            onClick={() => onInsert(`{${v.key}}`)}
            title={`Ex: ${v.example}`}
            className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 hover:bg-slate-100 hover:border-slate-300 text-slate-700"
            data-testid={`${testIdPrefix}-var-${v.key}`}
          >
            {v.label}
          </button>
        ))}
      </div>
      {unknown.length > 0 && (
        <div className="text-[10px] text-red-700" data-testid={`${testIdPrefix}-unknown-vars`}>
          🚫 Variáveis desconhecidas no seu texto: {unknown.map(v => `{${v}}`).join(", ")}. Remova antes de salvar/disparar.
        </div>
      )}
    </div>
  );
}

/* ============ Linha de cliente na listagem do preview do segmento ============ */
function ClientRow({ c, selected, toggleSel, channelOverride, waLinkFor, message, isRecent = false }) {
  const disabled = !c.has_phone || c.is_duplicate;
  const bg = isRecent
    ? "bg-amber-50/50 hover:bg-amber-100/60"
    : (disabled ? "opacity-60 bg-slate-50" : "hover:bg-accent/50");
  const lastSent = c.last_sent_at ? new Date(c.last_sent_at) : null;
  const lastSentPt = lastSent
    ? lastSent.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
    : null;
  return (
    <div
      className={`flex items-start gap-2 p-2 border-b last:border-0 text-xs ${bg}`}
      data-testid={`client-row-${c.client_id}`}
    >
      <Checkbox
        checked={selected.has(c.client_id)}
        onCheckedChange={() => !disabled && toggleSel(c.client_id)}
        disabled={disabled}
        data-testid={`select-${c.client_id}`}
        className="mt-0.5"
      />
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate flex items-center gap-1.5">
          {c.name || "(sem nome)"}
          {c.is_duplicate && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-orange-100 border border-orange-300 text-orange-800">
              duplicado
            </span>
          )}
          {c.dup_count > 1 && !c.is_duplicate && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 border border-slate-300 text-slate-700">
              {c.dup_count} cadastros mesmo tel
            </span>
          )}
          {isRecent && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 border border-amber-300 text-amber-800" data-testid={`recent-badge-${c.client_id}`}>
              já recebeu{lastSentPt ? ` · ${lastSentPt}` : ""}
            </span>
          )}
        </div>
        <div className="text-[10px] text-muted-foreground">
          {c.has_phone ? c.phone_masked : "sem telefone"}
          {c.skip_reason && !isRecent
            ? ` · ⚠ ${c.skip_reason === "duplicate_phone" ? "telefone duplicado" : c.skip_reason}`
            : ""}
        </div>
        {c.reasons && c.reasons.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {c.reasons.map((r, i) => (
              <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-700">
                {r}
              </span>
            ))}
          </div>
        )}
      </div>
      {channelOverride === "whatsapp_manual" && c.has_phone && !c.is_duplicate && (
        <a
          href={waLinkFor(c, message)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 h-7 px-2 text-[10px] rounded bg-emerald-500 hover:bg-emerald-600 text-white whitespace-nowrap shrink-0"
          data-testid={`open-wa-${c.client_id}`}
          onClick={(e) => {
            if (!message.trim()) {
              e.preventDefault();
              toast.error("Digite a mensagem antes de abrir o WhatsApp");
            }
          }}
        >
          <MessageSquare className="h-3 w-3" strokeWidth={1.5} />
          Abrir WhatsApp
        </a>
      )}
    </div>
  );
}

